import { isoDateTime, z } from "../zod.js";
import { Currency, Uuid } from "../identity/admin-schemas.js";
import {
  AgentCapKind,
  AgentCode,
  AgentDate,
  AgentGuardrailKind,
  AgentRunStatus,
  AgentToolImpact,
  AgentTriggerType,
} from "../agents/schemas.js";

// Module 14 agents domain events (spec 14 §6, D-514). Eleven producer names,
// each written to the outbox in the transaction of its change (D-514 ruling
// 4), with ids, codes and numbers and no sentence (D-225). Money appears only
// as `impact_minor` with `impact_currency`, which a consumer serves under the
// tool's value grants. The count here moves with
// `apps/api/src/modules/agents/agents-events.ts`, the agents block of
// `apps/api/test/unit/module-catalog-parity.test.ts` and
// `packages/contracts/test/agents-catalog.test.ts` (the four pin sites).
//
// Consumers named are the modules that will read each event: 12
// (notifications, P4) and 13 (analytics) are unbuilt, so no event here has a
// live consumer in 14-A; `agent.cap_reached` and `agent.guardrail_tripped` wait
// for 12-A (D-514 ruling 3).

const Timestamp = isoDateTime();
const Count = z.number().int().min(0);

/** §3.4: a run reached `completed`, `failed` or `cancelled`. */
export const AgentRunCompletedPayload = z
  .object({
    run_id: Uuid,
    agent_id: Uuid,
    agent_code: AgentCode,
    trigger_type: AgentTriggerType,
    status: AgentRunStatus,
    input_tokens: Count,
    output_tokens: Count,
    tool_calls_count: Count,
    actions_proposed_count: Count,
  })
  .strict();

/** §3.3 step 4: an action was inserted `proposed` (or turned `proposed` at execution). */
export const AgentActionProposedPayload = z
  .object({
    action_id: Uuid,
    run_id: Uuid.nullable(),
    agent_id: Uuid,
    tool_code: z.string(),
    impact_dimension: AgentToolImpact,
    impact_minor: z.number().int().optional(),
    impact_currency: Currency.optional(),
    impact_count: Count.optional(),
    facility_id: Uuid.nullable(),
    department_id: Uuid.nullable(),
    expires_at: Timestamp,
  })
  .strict();

/** §3.5: a person approved a proposal. */
export const AgentActionApprovedPayload = z
  .object({ action_id: Uuid, decided_by: Uuid, decided_at: Timestamp })
  .strict();

/** §3.5: a person rejected a proposal. */
export const AgentActionRejectedPayload = z
  .object({ action_id: Uuid, decided_by: Uuid, decided_at: Timestamp })
  .strict();

/** §3.5: a proposal passed `expires_at`, or its tool left the registry (D-509 ruling 9). */
export const AgentActionExpiredPayload = z
  .object({ action_id: Uuid, expired_at: Timestamp })
  .strict();

/** §3.5: an action executed, in the joined transaction of its effect (D-510 ruling 3). */
export const AgentActionExecutedPayload = z
  .object({
    action_id: Uuid,
    tool_code: z.string(),
    agent_id: Uuid,
    result_ref: z.string(),
    impact_minor: z.number().int().optional(),
    impact_currency: Currency.optional(),
    audit_log_id: Uuid.nullable(),
  })
  .strict();

/** §3.5: an execution failed; written by the second transaction after the rollback. */
export const AgentActionFailedPayload = z
  .object({ action_id: Uuid, tool_code: z.string(), error_code: z.string() })
  .strict();

/** §3.5: an executed action was undone (D-511 ruling 1). */
export const AgentActionUndonePayload = z
  .object({ action_id: Uuid, undo_action_id: Uuid.nullable(), undone_by: Uuid })
  .strict();

/** §3.3 step 5 and §2.2's token budget: a cap refused an unsupervised action
 * or a run. A spend cap's two values are minor units of `currency`, which it
 * always carries; an action or token cap counts and carries none (D-543
 * ruling 13). A consumer serves the spend values under the value grants of
 * the agent's money tools, as the Hub does. */
export const AgentCapReachedPayload = z
  .object({
    agent_id: Uuid,
    cap_kind: AgentCapKind,
    /** The tenant-zone month (`YYYY-MM`) for action and spend, the day for tokens. */
    period: z.string().regex(/^\d{4}-\d{2}(-\d{2})?$/),
    current_value: z.number().int(),
    cap_value: z.number().int(),
    currency: Currency.optional(),
  })
  .strict()
  .superRefine((p, issues) => {
    if ((p.cap_kind === "spend") !== (p.currency !== undefined)) {
      issues.addIssue({
        code: "custom",
        path: ["currency"],
        message: "a spend cap carries its currency, and only a spend cap does",
      });
    }
  });

/** §2.11: one per `agent_guardrail_events` row, in its transaction. */
export const AgentGuardrailTrippedPayload = z
  .object({
    guardrail_event_id: Uuid,
    kind: AgentGuardrailKind,
    agent_id: Uuid,
    run_id: Uuid.nullable(),
  })
  .strict();

/** §3.7: one per `agent_usage_daily` upsert, carrying the day's totals after it. */
export const AgentUsageRolledUpPayload = z
  .object({
    agent_id: Uuid,
    usage_date: AgentDate,
    input_tokens: Count,
    output_tokens: Count,
    estimated_cost_minor: Count,
  })
  .strict();

export const agentsProducerEvents = {
  "agent.run_completed": { payload: AgentRunCompletedPayload, consumers: ["13"] },
  "agent.action_proposed": { payload: AgentActionProposedPayload, consumers: ["12", "13"] },
  "agent.action_approved": { payload: AgentActionApprovedPayload, consumers: ["12", "13"] },
  "agent.action_rejected": { payload: AgentActionRejectedPayload, consumers: ["12", "13"] },
  "agent.action_expired": { payload: AgentActionExpiredPayload, consumers: ["12", "13"] },
  "agent.action_executed": { payload: AgentActionExecutedPayload, consumers: ["12", "13"] },
  "agent.action_failed": { payload: AgentActionFailedPayload, consumers: ["12", "13"] },
  "agent.action_undone": { payload: AgentActionUndonePayload, consumers: ["12", "13"] },
  "agent.cap_reached": { payload: AgentCapReachedPayload, consumers: ["12", "13"] },
  "agent.guardrail_tripped": { payload: AgentGuardrailTrippedPayload, consumers: ["12", "13"] },
  "agent.usage_rolled_up": { payload: AgentUsageRolledUpPayload, consumers: ["13"] },
} as const;

export type AgentsEventName = keyof typeof agentsProducerEvents;

export const agentsProducerEventNames = Object.keys(agentsProducerEvents) as AgentsEventName[];

/**
 * The events module 14 subscribes to in 14-A, exactly one (D-514 ruling 2):
 * the intake, deduplicated in `agents_processed_events` under consumer
 * `offer_agent.intake`. WP-E's consumer registers it; until then no
 * subscriber parity test reads this list.
 */
export const agentsConsumedEventNames = ["messaging.inbound_intake_created"] as const;
